import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import { ScrollToFormikError } from "../elements/ScrollToFormikError";
import { CalendarIcon } from "lucide-react";
import { motion } from "framer-motion";

const DatePickerInput = ({
    selected,
    onChange,
    showTimeSelect = false,
    showTimeSelectOnly = false,
    showMonthsOnly = false,
    timeInterval = 15,
    timeCaption = "Time",
    classes,
    dateFormat = 'dd-MM-yyyy',
    disabled = false,
    placeholder = "Select date",
    label,
    id,
    required = false,
    error,
    description,
    showYearDropdown = true,
    helperText,
    maxDate,
    minDate,
    allowFutureSelections = true,
    allowEarlierSelections = true,
    showMonthYearPicker = false,
    showMonthDropdown = true,
    showYearPicker = false,
    showYearOnly = false,
}: IDatePickerInput): JSX.Element => {
    const [selectedDate, setSelectedDate] = useState<Date | null>(selected);

    const fieldRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        onChange(selectedDate);
    }, [selectedDate])

    const carryDayToCalendarDate = (calendarDate: Date) => {
        const base = selectedDate ?? selected;
        if (!base) {
            setSelectedDate(calendarDate);
            return;
        }
        const targetYear = calendarDate.getFullYear();
        const targetMonth = calendarDate.getMonth();
        const desiredDay = base.getDate();
        const lastDayOfTargetMonth = new Date(targetYear, targetMonth + 1, 0).getDate();
        const day = Math.min(desiredDay, lastDayOfTargetMonth);
        const next = new Date(base);
        next.setFullYear(targetYear, targetMonth, day);
        if (minDate && next < minDate) return;
        if (maxDate && next > maxDate) return;
        if (!allowEarlierSelections && next < new Date(new Date().setHours(0, 0, 0, 0))) return;
        if (!allowFutureSelections && next > new Date(new Date().setHours(23, 59, 59, 999))) return;
        setSelectedDate(next);
    }

    return (
        <div className="w-full" ref={fieldRef}>
            {label && (
                <label
                    htmlFor={id}
                    className={clsx(
                        "block text-sm font-medium text-gray-700 dark:text-darkBg-mainText mb-1",
                        { ["text-danger-main"]: error && required }
                    )}
                >
                    {label} <span className="text-danger-main">{required && "*"}</span>
                    {description && (<p className={clsx("text-xs text-gray-400 dark:text-darkBg-subText italic", { ["text-danger-border"]: error && required })}>{description}</p>)}
                </label>
            )}
            <DatePicker
                selected={selected}
                className={clsx("relative w-full min-w-full border border-gray-300 dark:border-darkBg-border rounded-full shadow-sm py-2 text-left cursor-default",
                    "focus:outline-none focus:ring-1 focus:ring-primary-main focus:border-primary-border sm:text-sm",
                    "flex items-center px-2",
                    classes,
                    disabled ? "bg-gray-300 dark:bg-gray-700 cursor-not-allowed" : "bg-white dark:bg-darkBg",
                    selected && "dark:text-white",
                )}
                onChange={time => setSelectedDate(time)}
                onYearChange={(year) => carryDayToCalendarDate(year)}
                onMonthChange={(month) => carryDayToCalendarDate(month)}
                showTimeSelect={showTimeSelect}
                showTimeSelectOnly={showTimeSelectOnly}
                timeIntervals={timeInterval}
                timeCaption={timeCaption}
                showIcon={true}
                icon={<CalendarIcon className="h-4 w-4"/>}
                // filterTime={filterPassedTime}]
                maxDate={maxDate ? maxDate : !allowFutureSelections ? new Date : null}
                minDate={minDate ? minDate : !allowEarlierSelections ? new Date : null}
                dateFormat={showYearOnly ? 'yyyy' : showMonthsOnly ? 'MMMM yyyy' : dateFormat}
                // onInputClick={() => setSelectStartTime(false)}
                disabled={disabled}
                placeholderText={placeholder}
                showYearDropdown={showYearDropdown}
                showYearPicker={showYearPicker || showYearOnly}
                showMonthYearPicker={(showMonthYearPicker || showMonthsOnly) && !showYearOnly}
                showMonthDropdown={showMonthDropdown}
                useShortMonthInDropdown
                yearDropdownItemNumber={50}
                scrollableYearDropdown
                onChangeRaw={((e) => e.preventDefault())}
                calendarIconClassname="h-4 w-4"
            // withPortal
            />
            {/* Helper Text */}
            {/* <div className={clsx("text-", { ["text-danger-main"]: error })}>{helperText}</div> */}
               <div className={clsx('text-xs text-gray-400 dark:text-darkBg-subText', { ['!text-danger-main']: error })}>
                    {helperText && (
                      <motion.p
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-2 text-sm text-red-600 flex items-center font-medium"
                      >
                        <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {helperText && typeof helperText === 'string' ? helperText : undefined}
                      </motion.p>
                    )}
                  </div>
            <ScrollToFormikError
                fieldName={id ?? ""}
                fieldRef={fieldRef}
            />
        </div>
    )
}
export default DatePickerInput;

interface IDatePickerInput {
    id: string;
    selected: Date | null;
    onChange: (date: Date | null) => void;
    showTimeSelect?: boolean;
    showMonthsOnly?: boolean;
    showYearOnly?: boolean;
    timeInterval?: number;
    timeCaption?: string;
    showTimeSelectOnly?: boolean;
    classes?: string;
    dateFormat?: string;
    disabled?: boolean;
    placeholder?: string;
    label?: string;
    required?: boolean;
    error?: string;
    description?: string;
    showYearDropdown?: boolean;
    helperText?: string;
    maxDate?: Date;
    minDate?: Date;
    allowFutureSelections?: boolean;
    allowEarlierSelections?: boolean;
    showMonthYearPicker?: boolean;
    showMonthDropdown?: boolean;
    showYearPicker?: boolean;
}
